import React from "react";
import { formatOpeningBalance } from "../../helpers/financeAccounts";
import { FinanceAccountsSummary } from "./FinanceAccountsSummary";
import {
  FINANCE_CARD_STYLE,
  FINANCE_CARD_TITLE_STYLE,
  FINANCE_COMING_SOON_STYLE,
} from "./financeUi";

const METRIC_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
  gap: "14px",
};

const METRIC_VALUE = {
  fontSize: "22px",
  fontWeight: 800,
  margin: "0 0 2px",
  letterSpacing: "0.01em",
};

const METRIC_LABEL = {
  fontSize: "11px",
  opacity: 0.55,
  margin: "0 0 6px",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  fontWeight: 700,
};

const METRIC_HINT = {
  fontSize: "11px",
  opacity: 0.45,
  margin: 0,
};

function formatNetChangeToday(amount, currency = "CAD") {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency || "CAD",
      signDisplay: "exceptZero",
    }).format(safe);
  } catch {
    const prefix = safe > 0 ? "+" : "";
    return `${prefix}${safe.toFixed(2)} ${currency || "CAD"}`;
  }
}

/**
 * Shared Financial Position presentation — Finance + KPI + carousel.
 * Values must come from loadFinancePosition / computeFinancePosition only.
 */
export function FinancialPositionCard({
  position,
  loading = false,
  error = null,
  onOpenFinance,
  title = "Financial Position",
  showOpenHint = true,
  showAccountSummary = true,
  accountSummaryVariant = "compact",
}) {
  const currency = position?.currency || "CAD";
  const clickable = typeof onOpenFinance === "function";
  const moneyInToday = Number(position?.moneyInToday) || 0;
  const moneyOutToday = Number(position?.moneyOutToday) || 0;
  const netChangeToday = moneyInToday - moneyOutToday;

  const totals = error ? (
    <p style={{ color: "#f87171", margin: 0 }}>{error}</p>
  ) : loading ? (
    <p style={FINANCE_COMING_SOON_STYLE}>Loading…</p>
  ) : (
    <div style={METRIC_GRID}>
      <div>
        <p style={METRIC_LABEL}>Current Cash</p>
        <p style={{ ...METRIC_VALUE, color: "#fff" }}>
          {formatOpeningBalance(position?.currentCash ?? 0, currency)}
        </p>
      </div>
      <div>
        <p style={METRIC_LABEL}>Available Cash</p>
        <p style={{ ...METRIC_VALUE, color: "#4ee2a1" }}>
          {formatOpeningBalance(position?.availableCash ?? 0, currency)}
        </p>
        <p style={METRIC_HINT}>What you can safely spend</p>
      </div>
      <div>
        <p style={METRIC_LABEL}>Reserved Cash</p>
        <p style={{ ...METRIC_VALUE, color: "#c9a84c" }}>
          {formatOpeningBalance(position?.reservedCash ?? 0, currency)}
        </p>
        <p style={METRIC_HINT}>Tax reserve accounts</p>
      </div>
      <div>
        <p style={METRIC_LABEL}>Sales Tax Held</p>
        <p style={{ ...METRIC_VALUE, color: "#c9a84c" }}>
          {formatOpeningBalance(position?.salesTaxHeld ?? 0, currency)}
        </p>
        <p style={METRIC_HINT}>Collected on paid invoices</p>
      </div>
      <div>
        <p style={METRIC_LABEL}>Money In Today</p>
        <p style={{ ...METRIC_VALUE, color: "#4ee2a1" }}>
          {formatOpeningBalance(moneyInToday, currency)}
        </p>
      </div>
      <div>
        <p style={METRIC_LABEL}>Money Out Today</p>
        <p style={{ ...METRIC_VALUE, color: "#f87171" }}>
          {formatOpeningBalance(moneyOutToday, currency)}
        </p>
      </div>
      <div>
        <p style={METRIC_LABEL}>Net Change Today</p>
        <p
          style={{
            ...METRIC_VALUE,
            color:
              netChangeToday > 0
                ? "#4ee2a1"
                : netChangeToday < 0
                  ? "#f87171"
                  : "#fff",
          }}
        >
          {formatNetChangeToday(netChangeToday, currency)}
        </p>
      </div>
    </div>
  );

  const accountBlock =
    showAccountSummary && !error ? (
      <FinanceAccountsSummary
        accounts={position?.accounts || []}
        currency={currency}
        loading={loading}
        variant={accountSummaryVariant}
        embedded
        title="By account"
      />
    ) : null;

  const body = (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <h3
          className="finance-card-title dashboard-widget-title"
          style={{ ...FINANCE_CARD_TITLE_STYLE, margin: 0 }}
        >
          {title}
        </h3>
        {clickable && showOpenHint ? (
          <span
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#4ee2a1",
              whiteSpace: "nowrap",
            }}
          >
            Open Finance →
          </span>
        ) : null}
      </div>
      {totals}
      {accountBlock}
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onOpenFinance}
        className="kpi-snapshot-card dashboard-widget-card financial-position-card"
        style={{
          ...FINANCE_CARD_STYLE,
          width: "100%",
          textAlign: "left",
          color: "inherit",
          cursor: "pointer",
          font: "inherit",
        }}
        aria-label="Open Finance module"
      >
        {body}
      </button>
    );
  }

  return (
    <div
      className="kpi-snapshot-card dashboard-widget-card financial-position-card"
      style={FINANCE_CARD_STYLE}
    >
      {body}
    </div>
  );
}
